import {useState, useEffect, useRef, useCallback} from 'react';
import {view} from '@sail/ui';
import {qrCode} from '@sail/icons/react/Icon';

import type {PaymentOption} from 'src/v2/state/types';
import {Text, ChipButton} from 'src/v2/components/common';
import {setValue, useSelector} from 'src/v2/state/store';
import {formatCurrencyAmountToMajorUnitsFixed} from 'src/utils/helpers';
import {useWalletPress} from 'src/v2/components/walletSelect/useWalletPress';
import {message, useIntl} from '@sail/intl';
import {SearchInput} from './SearchInput';
import {WalletGrid} from './WalletGrid';
import {WalletIcon} from '../wallet/WalletIcon';
import {useWalletGridKeyboardNavigation} from './useWalletGridKeyboardNavigation';
import {WALLET_GRID_COLUMNS} from './constants';

const messages = {
  placeholder: message({
    id: 'crypto.payins.v2.wallet_select.search_placeholder',

    description:
      'Placeholder text for the search results in the wallet select grid',

    defaultMessage: 'Over 400 wallets',
  }),

  noWalletFound: message({
    id: 'crypto.payins.v2.wallet_select.search_no_wallet_found',
    description: 'Message when search returns no wallet results',
    defaultMessage: 'No wallet found.',
  }),

  noWalletFoundDirectDepositDescription: message<{
    amount: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.wallet_select.search_no_wallet_direct_deposit',

    description:
      'Description suggesting direct deposit when no wallet search results',

    defaultMessage:
      'You can still complete this payment by sending {amount} via direct deposit.',
  }),

  directDeposit: message({
    id: 'crypto.payins.v2.wallet_select.search_direct_deposit',

    description:
      'Label for the direct deposit via QR code option in the wallet select grid',

    defaultMessage: 'Direct deposit via QR code',
  }),
};

interface Props {
  walletOptions: PaymentOption[];
  onClose: () => void;
}

const MAX_WALLETS = 8;

const PLACEHOLDER_WALLETS = [
  {provider: 'Phantom', name: 'Phantom'},
  {provider: 'MetaMask', name: 'MetaMask'},
  {provider: 'Coinbase Wallet', name: 'Coinbase Wallet'},
] as const;

export const WalletSearchGrid = ({walletOptions, onClose}: Props) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchContentVisible, setSearchContentVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const depositAmount = useSelector((state) => state.paymentDetails.amount);
  const depositCurrency = useSelector((state) => state.paymentDetails.currency);
  const directDepositEnabled = useSelector(
    (state) =>
      state.featureFlags.enable_crypto_payins_direct_deposit &&
      state.settlementPartner === 'bridge' &&
      !state.setupFutureUsage,
  );
  const directDepositAnyCryptoFlowEnabled = useSelector(
    (state) => state.featureFlags.enable_direct_deposit_any_crypto_flow,
  );

  const formattedAmount = `$${formatCurrencyAmountToMajorUnitsFixed(
    depositAmount,
    depositCurrency,
  )}`;

  const filteredWalletOptions = walletOptions
    .filter(({name}) => name.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, MAX_WALLETS);

  const onCancelSearch = useCallback(() => {
    setSearchContentVisible(false);

    setTimeout(() => {
      onClose();
    }, 150);
  }, [onClose]);

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  const {focusedIndex, setFocusedIndex, handleKeyDown, resetFocus} =
    useWalletGridKeyboardNavigation({
      itemCount: filteredWalletOptions.length,
      columns: WALLET_GRID_COLUMNS,
      onClose: onCancelSearch,
      onFocusSearchInput: focusSearchInput,
    });

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      resetFocus();
    },
    [resetFocus],
  );

  const handleWalletPress = useWalletPress('wallet_grid');

  const handleDirectDepositClick = useCallback(() => {
    setValue((state) => {
      if (directDepositAnyCryptoFlowEnabled) {
        state.screen = 'TOKEN_SELECT';
      } else {
        state.screen = 'DIRECT_DEPOSIT';
      }
    });
  }, [directDepositAnyCryptoFlowEnabled]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchContentVisible(true), 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const renderContent = () => {
    if (!searchQuery) {
      return (
        <view.div
          css={{
            stack: 'y',
            gap: 'space.150',
            alignX: 'center',
            alignY: 'center',
          }}
        >
          <view.div
            css={{
              stack: 'x',
              gap: 'small',
              alignX: 'center',
            }}
          >
            {PLACEHOLDER_WALLETS.map((wallet) => (
              <WalletIcon
                key={wallet.name}
                walletOption={wallet}
                width="24px"
                height="24px"
                borderRadius="small"
              />
            ))}
          </view.div>
          <Text type="caption">{intl.formatMessage(messages.placeholder)}</Text>
        </view.div>
      );
    }

    if (filteredWalletOptions.length === 0) {
      return (
        <view.div
          css={{
            stack: 'y',
            gap: 'xsmall',
            alignX: 'center',
            alignY: 'center',
          }}
        >
          <Text type="heading">
            {intl.formatMessage(messages.noWalletFound)}
          </Text>
          {directDepositEnabled ? (
            <>
              <Text type="body" css={{textAlign: 'center'}}>
                {intl.formatMessage(
                  messages.noWalletFoundDirectDepositDescription,
                  {amount: formattedAmount},
                )}
              </Text>
              <view.div css={{marginTop: 'space.150'}}>
                <ChipButton
                  icon={qrCode}
                  onClick={handleDirectDepositClick}
                  label={intl.formatMessage(messages.directDeposit)}
                />
              </view.div>
            </>
          ) : null}
        </view.div>
      );
    }

    return (
      <view.div
        css={{
          display: 'grid',
          width: 'fill',
          gridTemplateColumns: `repeat(${WALLET_GRID_COLUMNS}, 1fr)`,
          alignItems: 'start',
          gapY: 'space.150',
        }}
      >
        <WalletGrid
          walletOptions={filteredWalletOptions}
          onWalletPress={handleWalletPress}
          focusedIndex={focusedIndex}
          onFocusChange={setFocusedIndex}
        />
      </view.div>
    );
  };

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'space.150',
        minHeight: '244px',
        paddingBottom: 'space.150',
        opacity: searchContentVisible ? 1 : 0,
        transition: 'opacity 150ms ease-in-out',
      }}
      onKeyDown={handleKeyDown}
    >
      <view.div
        css={{
          paddingTop: 'space.150',
          paddingBottom: 'small',
          paddingX: 'xsmall',
          height: 'fit',
        }}
      >
        <SearchInput
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          onClose={onCancelSearch}
          contentVisible={searchContentVisible}
          onKeyDown={handleKeyDown}
          onFocus={resetFocus}
          inputRef={searchInputRef}
        />
      </view.div>
      {renderContent()}
    </view.div>
  );
};
